import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SubHeaderService } from '../../../../../shared/store/subheader/subheader.service';
import { BreadcrumbService } from 'xng-breadcrumb';
import { ViewService } from '../../../../../shared/store/view/views.service';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormService } from '../../../../../shared/store/form/form.service';
import { filter, Observable } from 'rxjs';
import { View } from '../../../../../shared/store/view/types';
import {
  ClaimTypeDocumentTypeModel,
  ClaimTypeModel,
} from '../../../../../shared/store/claim-type/types';
import { ClaimTypeService } from '../../../../../shared/store/claim-type/claim-type.service';
import { SourceEnum } from '../../../../../shared/store/typeahead/types';
import { ModalService } from '../../../../../shared/service/modal.service';
import { ModalContentComponent } from '../../../../../shared/components/modal-content/modal-content.component';
import { ActionButton } from '../../../../../shared/components/modal-content/action-button';
import { take } from 'rxjs/operators';
import { getAmountSign } from '../../../../../shared/helper/amounthelper';
import { AmountType } from '../../../../../shared/store/commissions/commission-plan-rule/types';
import { DocumentTypeExtendedModel } from '../../../../../shared/store/document-type/types';
import { ModalComponentKey } from 'src/app/shared/service/modal-component';

@UntilDestroy()
@Component({
  selector: 'app-claim-type-form-content',
  templateUrl: './claim-type-form-content.component.html',
  styleUrls: ['./claim-type-form-content.component.scss'],
  standalone: false,
})
export class ClaimTypeFormContentComponent implements OnInit, OnDestroy {
  view$: Observable<View>;
  claimTypeModel$: Observable<ClaimTypeModel>;
  claimTypeInstanceModel$: Observable<ClaimTypeModel>;

  claimTypeForm: FormGroup;

  isEditMode: boolean = false;
  claimTypeId: string;

  defaultAmountType: AmountType = 'Monetary';
  amountPlaceholder: string;
  amountSuffix: string;

  /** Grid rows of the linked document types, kept in sync with the `documentTypes` FormArray. */
  documentTypesValues: { documentTypeName: string; mandatory: boolean }[] = [];

  constructor(
    private readonly claimTypeService: ClaimTypeService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly subHeaderService: SubHeaderService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly viewService: ViewService,
    private readonly formBuilder: FormBuilder,
    private readonly formService: FormService,
    private readonly modalService: NgbModal,
    private readonly modalExchangeService: ModalService,
  ) {
    this.view$ = this.viewService.getViews.value$;
    this.claimTypeModel$ = this.claimTypeService.getById.value$;
    this.claimTypeInstanceModel$ = this.claimTypeService.createInstance.value$;
  }

  ngOnInit(): void {
    //Initialisation du suffixe et du placeholder
    this.amountPlaceholder = this.amountSuffix = getAmountSign(
      this.defaultAmountType,
    );

    // Reactive form declaration
    this.claimTypeForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      productCategoryId: [null, [Validators.required]],
      sequence: [0],
      active: [true],
      defaultFeeAmount: [null],
      // Linked documents managed through a modal — only the selected ones are kept.
      documentTypes: this.formBuilder.array([]),
    });

    this.documentTypes.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => this.refreshDocumentTypesValues());

    // Determine mode from the route id (Edit = true, Create = false)
    this.claimTypeId = this.route.snapshot.params['id'];
    this.isEditMode = !!this.claimTypeId;

    if (this.isEditMode) {
      this.claimTypeService.getById.call(+this.claimTypeId);
    } else {
      this.claimTypeService.createInstance.call();
    }

    this.subHeaderService
      .getTriggerAction()
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        if (x == 'claimTypeCreate' || x == 'claimTypeUpdate') this.onSubmit();
        if (x == 'claimTypeList') {
          this.formService.clear();
          this.router.navigate(['/Insurance/ClaimTypes/']);
        }
        if (x == 'claimTypeDetails') {
          this.formService.clear();
          this.router.navigate([
            '/Insurance/ClaimTypes/Details',
            this.claimTypeId,
          ]);
        }
        this.subHeaderService.setTriggerAction('');
      });

    this.view$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@list', x.title);

        if (!this.isEditMode)
          this.breadcrumbService.set('@claimTypeCreate', x.breadcrumbCreate);
      });

    if (this.isEditMode) {
      this.claimTypeModel$
        .pipe(
          filter((x) => !!x),
          take(1),
        )
        .subscribe((x) => {
          this.breadcrumbService.set('@claimTypeEdit', x.name);
          this.patchForm(x);
        });
    } else {
      this.claimTypeInstanceModel$
        .pipe(
          filter((x) => !!x),
          take(1),
        )
        .subscribe((x) => {
          this.patchForm(x);
        });
    }
  }

  private patchForm = (x: ClaimTypeModel) => {
    this.claimTypeForm.patchValue({
      name: x.name,
      productCategoryId:
        x.productCategoryId == 0 ? undefined : x.productCategoryId,
      sequence: x.sequence,
      active: x.active,
      defaultFeeAmount: x.defaultFeeAmount ?? null,
    });

    const documentTypes = x.claimTypeDocumentTypes ?? [];
    this.documentTypes.clear();
    documentTypes.forEach((ctdt) =>
      this.documentTypes.push(this.buildDocumentGroup(ctdt)),
    );
  };

  private buildDocumentGroup(document?: ClaimTypeDocumentTypeModel): FormGroup {
    return this.formBuilder.group({
      id: [document?.id ?? 0],
      documentTypeId: [document?.documentTypeId ?? 0],
      documentTypeName: [document?.documentTypeName ?? ''],
      mandatory: [document?.mandatory ?? true],
    });
  }

  get documentTypes(): FormArray {
    return this.claimTypeForm.controls['documentTypes'] as FormArray;
  }

  /**
   * Rebuilds the grid rows from the FormArray. Kept in a field rather than exposed as a getter: a
   * getter returns a new array on every change detection cycle, and the grid bound to it would
   * reset its rows in a loop.
   */
  private refreshDocumentTypesValues() {
    this.documentTypesValues = this.documentTypes.value.map((documentType) => ({
      documentTypeName: documentType.documentTypeName,
      mandatory: documentType.mandatory,
    }));
  }

  /**
   * Opens the shared modal to pick the document types (and their mandatory flag) expected for this
   * claim type, then replaces the document rows with the modal's result.
   */
  openClaimTypeDocumentsModal() {
    let documentTypes: DocumentTypeExtendedModel[] = [];

    this.claimTypeForm.value.documentTypes.forEach((x) => {
      const documentType: DocumentTypeExtendedModel = {
        id: x.documentTypeId,
        name: x.documentTypeName,
        description: '',
        mandatory: x.mandatory,
      };
      documentTypes.push(documentType);
    });

    const modalRef = this.modalService.open(ModalContentComponent, {
      size: 'lg',
      backdrop: 'static',
    });

    modalRef.componentInstance.component =
      this.modalExchangeService.getComponentClass(
        ModalComponentKey.SelectClaimTypeDocumentTypeEdit,
      );
    modalRef.componentInstance.title = 'CLAIM_TYPE_DOCUMENT_TYPE_LBL_TITLE';
    modalRef.componentInstance.cancelButton = true;
    modalRef.componentInstance.buttons = [
      <ActionButton>{
        actionName: 'claimTypeDocumentTypesUpdate',
      },
    ];
    modalRef.componentInstance.inputParams = {
      source: SourceEnum.Modal,
      selectedDocumentTypes: documentTypes,
    };

    // The selection comes back as the modal result; a dismiss rejects the promise, hence the catch.
    modalRef.result
      .then((selection: DocumentTypeExtendedModel[]) =>
        this.applyDocumentTypes(selection),
      )
      .catch(() => {});
  }

  /**
   * Rebuilds the document rows from the modal selection. A document that was already linked keeps
   * its link id — the modal only knows document types — while the modal wins on the mandatory flag.
   */
  private applyDocumentTypes(selection: DocumentTypeExtendedModel[]) {
    const values = this.claimTypeForm.value.documentTypes;

    const rows = selection.map((documentType) => {
      const existing = values.find(
        (dt) => dt.documentTypeId === documentType.id,
      );

      return {
        id: existing?.id ?? 0,
        documentTypeId: documentType.id,
        documentTypeName: documentType.name,
        mandatory: documentType.mandatory ?? existing?.mandatory ?? true,
      };
    });

    this.documentTypes.clear();
    rows.forEach(() => this.documentTypes.push(this.buildDocumentGroup()));
    this.documentTypes.patchValue(rows);
  }

  onSubmit() {
    if (this.claimTypeForm.invalid) {
      this.claimTypeForm.markAllAsTouched();
      this.formService.setEntityErrors('CLAIM_TYPE');
      this.formService.countErrors(this.claimTypeForm, true);
      return;
    }

    const claimTypeId = this.claimTypeId ? +this.claimTypeId : 0;

    const documentTypes: ClaimTypeDocumentTypeModel[] =
      this.documentTypes.value.map((documentType) => ({
        id: documentType.id ?? 0,
        claimTypeId: claimTypeId,
        documentTypeId: documentType.documentTypeId,
        mandatory: documentType.mandatory,

        //others
        claimTypeName: '',
        documentTypeName: '',
      }));

    const claimTypeModel: ClaimTypeModel = {
      id: claimTypeId,
      name: this.claimTypeForm.value.name!,
      productCategoryId: this.claimTypeForm.value.productCategoryId!,
      sequence: this.claimTypeForm.value.sequence ?? 0,
      active: this.claimTypeForm.value.active,
      defaultFeeAmount: this.claimTypeForm.value.defaultFeeAmount ?? null,
      claimTypeDocumentTypes: documentTypes,
    };

    let result$: Observable<ClaimTypeModel>;
    if (this.isEditMode)
      result$ = this.claimTypeService.update.call(claimTypeModel);
    else result$ = this.claimTypeService.add.call(claimTypeModel);

    result$
      .pipe(
        filter((x) => !!x),
        take(1),
      )
      .subscribe((result) => {
        this.formService.clear();
        this.router.navigate(['/Insurance/ClaimTypes/Details', result.id]);
      });
  }

  ngOnDestroy(): void {
    this.claimTypeService.add.reset();
    this.claimTypeService.update.reset();

    if (this.isEditMode) {
      this.claimTypeService.getById.reset();
    }

    this.breadcrumbService.set('@claimTypeEdit', ' ');
    this.breadcrumbService.set('@claimTypeCreate', ' ');
  }
}
